import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import {
  useActivityActions,
  useActivityFeedData,
  useActivityNotificationsData,
} from "../contexts/ActivityContext";
import { useAuth } from "../contexts/AuthContext";
import {
  ArrowLeftIcon,
  UserPlusIcon,
  MessageSquareIcon,
  CheckCircleIcon,
  UserCheckIcon,
  LogOutIcon,
  XCircleIcon,
  Edit2Icon,
  CameraIcon,
  StarIcon,
  UserXIcon,
  Trash2Icon,
  EyeIcon,
  CheckIcon,
  BellIcon,
} from "../components/icons/FeatherIcons";
import { type Notification } from "../types";
import { usePageTracking } from "@/hooks/usePageTracking";
import { crashlytics, db } from "@/services/firebase";
import { fallbackImage } from "../utils/getCachedImage";
import { useCachedImage } from "../hooks/useCachedImage";
import { doc, updateDoc, serverTimestamp } from "firebase/firestore";

const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
  if (e.currentTarget.src !== fallbackImage) {
    e.currentTarget.src = fallbackImage;
  }
};

export const NotificationItem: React.FC<{ notification: Notification }> = ({
  notification,
}) => {
  const navigate = useNavigate();
  const { acceptFollowRequest, declineFollowRequest } = useAuth();
  const { activities } = useActivityFeedData();
  const {
    markNotificationAsRead,
    refreshSingleActivity,
    approveRejoinRequest,
    declineRejoinRequest,
    respondToInvite,
  } = useActivityActions();
  const [isProcessing, setIsProcessing] = useState(false);
  const [optimisticResolution, setOptimisticResolution] = useState<
    "accepted" | "declined" | null
  >(null);
  const [activityTitle, setActivityTitle] = useState<string | null>(null);

  // Cache user profile image
  const cachedProfileImage = useCachedImage(
    notification.fromUser.profileImageUrl,
    notification.fromUser.username,
    notification.fromUser.id,
  );

  // Check if this is a grouped notification
  const isGrouped = (notification as any).grouped;
  const allNotifications = (notification as any).allNotifications as
    | Notification[]
    | undefined;

  // Fetch activity title if this is an activity notification
  useEffect(() => {
    let cancelled = false;
    const fetchActivityTitle = async () => {
      if (!notification.activityId) {
        return;
      }
      // First try to find in existing activities
      const existingActivity = activities.find(
        (a) => a.id === notification.activityId,
      );
      if (existingActivity) {
        if (!cancelled) {
          setActivityTitle(existingActivity.title);
        }
        return;
      }
      // If not found, refresh the specific activity
      const freshActivity = await refreshSingleActivity(
        notification.activityId,
      );
      if (freshActivity && !cancelled) {
        setActivityTitle(freshActivity.title);
      }
    };
    if (isGrouped && notification.activityId) {
      fetchActivityTitle();
    }
    return () => {
      cancelled = true;
    };
  }, [notification.activityId, activities, refreshSingleActivity, isGrouped]);

  const getIcon = () => {
    switch (notification.type) {
      case "JOIN":
        return { Icon: UserPlusIcon, color: "text-primary" };
      case "MESSAGE":
        return { Icon: MessageSquareIcon, color: "text-blue-400" };
      case "FOLLOW_REQUEST":
        return { Icon: UserPlusIcon, color: "text-yellow-500" };
      case "FOLLOW_ACCEPT":
        return { Icon: UserCheckIcon, color: "text-green-500" };
      case "FOLLOW_DECLINE":
        return { Icon: XCircleIcon, color: "text-red-500" };
      case "REJOIN_REQUEST":
        return { Icon: UserPlusIcon, color: "text-orange-500" };
      case "REJOIN_APPROVED":
        return { Icon: UserCheckIcon, color: "text-green-500" };
      case "REJOIN_DECLINED":
        return { Icon: XCircleIcon, color: "text-red-500" };
      case "ACTIVITY_INVITE":
        return { Icon: UserPlusIcon, color: "text-blue-500" };
      case "ACTIVITY_INVITE_ACCEPT":
        return { Icon: UserCheckIcon, color: "text-green-500" };
      case "ACTIVITY_INVITE_DECLINE":
        return { Icon: XCircleIcon, color: "text-red-500" };
      case "ACTIVITY_INVITE_CANCEL":
        return { Icon: XCircleIcon, color: "text-gray-500" };
      case "MATCH_SUGGESTION":
        return { Icon: StarIcon, color: "text-purple-500" };
      case "LEAVE":
        return { Icon: LogOutIcon, color: "text-red-500" };
      case "ACTIVITY_UPDATE":
        return { Icon: Edit2Icon, color: "text-purple-500" };
      case "ACTIVITY_DELETE":
        return { Icon: Trash2Icon, color: "text-red-600" };
      case "NEW_STORY":
        return { Icon: CameraIcon, color: "text-pink-500" };
      case "ACTIVITY_KICK":
        return { Icon: XCircleIcon, color: "text-red-600" };
      case "MUTUAL_MATCH":
        return { Icon: StarIcon, color: "text-yellow-500" };
      case "ACTIVITY_FULL":
        return { Icon: CheckCircleIcon, color: "text-green-600" };
      case "DIRECT_CHAT":
        return { Icon: MessageSquareIcon, color: "text-indigo-500" };
      case "FOLLOWER_REMOVED":
        return { Icon: UserXIcon, color: "text-red-500" };
      case "ACCOUNT_DELETION":
        return { Icon: UserXIcon, color: "text-red-500" };
      default:
        return { Icon: CheckCircleIcon, color: "text-gray-400" };
    }
  };

  const { Icon, color } = getIcon();
  const isActionableNotification =
    notification.type === "FOLLOW_REQUEST" ||
    notification.type === "REJOIN_REQUEST" ||
    notification.type === "ACTIVITY_INVITE" ||
    notification.type === "MATCH_SUGGESTION";
  const isLocallyHandled = optimisticResolution !== null;
  const isHandled = notification.isRead || isLocallyHandled;
  // Show as unread if any notification in the group is unread
  const hasUnread =
    isGrouped && allNotifications
      ? allNotifications.some((n) => !n.isRead)
      : !isHandled;
  const bgColor = hasUnread ? "bg-primary/5" : "bg-white";

  const getHandledLabel = () => {
    if (optimisticResolution === "accepted") {
      if (notification.type === "REJOIN_REQUEST") return "Request approved";
      if (notification.type === "MATCH_SUGGESTION") return "Suggestion accepted";
      return "Accepted";
    }

    if (optimisticResolution === "declined") {
      if (notification.type === "FOLLOW_REQUEST") return "Request declined";
      if (notification.type === "REJOIN_REQUEST") return "Request declined";
      if (notification.type === "ACTIVITY_INVITE") return "Invite declined";
      if (notification.type === "MATCH_SUGGESTION") return "Suggestion declined";
    }

    if (notification.type === "MATCH_SUGGESTION") return "Suggestion handled";
    if (notification.type === "ACTIVITY_INVITE") return "Invite handled";
    return "Request handled";
  };

  const handleAccept = async () => {
    setIsProcessing(true);
    setOptimisticResolution("accepted");
    try {
      if (notification.type === "FOLLOW_REQUEST") {
        await acceptFollowRequest(notification.fromUser.id);
        markNotificationAsRead(notification.id);
      } else if (
        notification.type === "REJOIN_REQUEST" &&
        notification.activityId
      ) {
        await approveRejoinRequest(
          notification.activityId,
          notification.fromUser.id,
        );
        markNotificationAsRead(notification.id);
      } else if (
        notification.type === "ACTIVITY_INVITE" &&
        notification.activityId
      ) {
        await respondToInvite(notification.activityId, true);
        markNotificationAsRead(notification.id);
      } else if (
        notification.type === "MATCH_SUGGESTION" &&
        notification.proposalId &&
        db
      ) {
        await updateDoc(doc(db, "matchProposals", notification.proposalId), {
          status: "accepted",
          acceptedAt: serverTimestamp(),
        });
        markNotificationAsRead(notification.id);
        navigate(`/create?proposalId=${notification.proposalId}`);
      }
    } catch (error) {
      setOptimisticResolution(null);
      crashlytics.logError(
        `Error accepting request: ${JSON.stringify(error?.message)}`,
        "NotificationsPage.tsx",
        error,
      );
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDecline = async () => {
    setIsProcessing(true);
    setOptimisticResolution("declined");
    try {
      if (notification.type === "FOLLOW_REQUEST") {
        await declineFollowRequest(notification.fromUser.id);
        markNotificationAsRead(notification.id);
      } else if (
        notification.type === "REJOIN_REQUEST" &&
        notification.activityId
      ) {
        await declineRejoinRequest(
          notification.activityId,
          notification.fromUser.id,
        );
        markNotificationAsRead(notification.id);
      } else if (
        notification.type === "ACTIVITY_INVITE" &&
        notification.activityId
      ) {
        await respondToInvite(notification.activityId, false);
        markNotificationAsRead(notification.id);
      } else if (
        notification.type === "MATCH_SUGGESTION" &&
        notification.proposalId &&
        db
      ) {
        await updateDoc(doc(db, "matchProposals", notification.proposalId), {
          status: "declined",
          declinedAt: serverTimestamp(),
        });
        markNotificationAsRead(notification.id);
      }
    } catch (error) {
      setOptimisticResolution(null);
      crashlytics.logError(
        `Error declining request: ${JSON.stringify(error?.message)}`,
        "NotificationsPage.tsx",
        error,
      );
    } finally {
      setIsProcessing(false);
    }
  };

  const handleNotificationClick = () => {
    // Don't mark actionable notifications as read on click - only when user takes action
    if (isActionableNotification && !isHandled) {
      // Don't mark as read - user needs to take action first
      return;
    }

    // Mark all notifications in the group as read
    if (isGrouped && allNotifications) {
      allNotifications.forEach((n) => {
        // Skip actionable notifications in groups too
        const isActionable =
          n.type === "FOLLOW_REQUEST" ||
          n.type === "REJOIN_REQUEST" ||
          n.type === "ACTIVITY_INVITE" ||
          n.type === "MATCH_SUGGESTION";
        if (!n.isRead && !isActionable) {
          markNotificationAsRead(n.id);
        }
      });
    } else if (!notification.isRead) {
      markNotificationAsRead(notification.id);
    }
  };

  // Count only unread notifications in the group
  const unreadCount =
    isGrouped && allNotifications
      ? allNotifications.filter((n) => !n.isRead).length
      : !isHandled
        ? 1
        : 0;

  // For grouped notifications, show a summary with activity title (only unread)
  const displayText =
    isGrouped && allNotifications && unreadCount > 1
      ? `${unreadCount} new updates for Activity: ${activityTitle || "this activity"}`
      : notification.text;

  return (
    <div
      className={`p-3 rounded-lg transition-all duration-300 ${bgColor} border border-gray-200`}
    >
      <div className="flex items-center space-x-3">
        <Link
          to={`/profile/${notification.fromUser.id}`}
          className="flex-shrink-0 relative"
          onClick={handleNotificationClick}
        >
          <img
            onError={handleImageError}
            src={cachedProfileImage}
            alt={notification.fromUser.username}
            className="w-12 h-12 rounded-2xl"
          />
          <div className="absolute -bottom-1 -right-1 bg-white p-1 rounded-full shadow-sm">
            <Icon className={`w-5 h-5 ${color}`} />
          </div>
        </Link>

        <div className="flex-1 min-w-0">
          <Link
            to={notification.link}
            className="hover:underline"
            onClick={handleNotificationClick}
          >
            <p
              className="text-gray-800 text-sm line-clamp-2"
              dangerouslySetInnerHTML={{ __html: displayText }}
            ></p>
          </Link>
          <div className="flex items-center gap-2 mt-1">
            <p className="text-xs text-gray-500">
              {new Date(notification.createdAt).toLocaleString("en-US", {
                weekday: "short",
                month: "short",
                day: "numeric",
                hour: "numeric",
                minute: "2-digit",
                hour12: true,
              })}
            </p>
            {isGrouped && allNotifications && unreadCount > 1 && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                {unreadCount} new
              </span>
            )}
            {!isGrouped && notification.count && notification.count > 1 && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
                {notification.count}×
              </span>
            )}
          </div>
          {isActionableNotification && isHandled && (
            <p className="text-xs text-gray-400 mt-1 italic">{getHandledLabel()}</p>
          )}
        </div>

        {hasUnread &&
          !isActionableNotification && (
            <div className="w-2.5 h-2.5 bg-primary rounded-full self-center flex-shrink-0 ml-2"></div>
          )}
      </div>

      {notification.type === "FOLLOW_REQUEST" && !isHandled && (
        <div className="flex items-center space-x-2 mt-2 pl-14">
          <button
            onClick={handleAccept}
            disabled={isProcessing}
            className="flex-1 bg-primary text-white text-sm font-semibold py-1.5 rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isProcessing ? "Processing..." : "Accept"}
          </button>
          <button
            onClick={handleDecline}
            disabled={isProcessing}
            className="flex-1 bg-gray-200 text-gray-800 text-sm font-semibold py-1.5 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isProcessing ? "Processing..." : "Decline"}
          </button>
        </div>
      )}

      {notification.type === "REJOIN_REQUEST" && !isHandled && (
        <div className="flex items-center space-x-2 mt-2 pl-14">
          <button
            onClick={handleAccept}
            disabled={isProcessing}
            className="flex-1 bg-primary text-white text-sm font-semibold py-1.5 rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isProcessing ? "Processing..." : "Approve"}
          </button>
          <button
            onClick={handleDecline}
            disabled={isProcessing}
            className="flex-1 bg-gray-200 text-gray-800 text-sm font-semibold py-1.5 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isProcessing ? "Processing..." : "Decline"}
          </button>
        </div>
      )}

      {notification.type === "ACTIVITY_INVITE" && !isHandled && (
        <div className="flex items-center space-x-2 mt-2 pl-14">
          <button
            onClick={handleAccept}
            disabled={isProcessing}
            className="flex-1 bg-primary text-white text-sm font-semibold py-1.5 rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isProcessing ? "Processing..." : "Accept"}
          </button>
          <button
            onClick={handleDecline}
            disabled={isProcessing}
            className="flex-1 bg-gray-200 text-gray-800 text-sm font-semibold py-1.5 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isProcessing ? "Processing..." : "Decline"}
          </button>
        </div>
      )}

      {notification.type === "MATCH_SUGGESTION" && !isHandled && (
        <div className="flex items-center space-x-2 mt-2 pl-14">
          <button
            onClick={handleAccept}
            disabled={isProcessing}
            className="flex-1 bg-primary text-white text-sm font-semibold py-1.5 rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isProcessing ? "Processing..." : "Yes, create it"}
          </button>
          <button
            onClick={handleDecline}
            disabled={isProcessing}
            className="flex-1 bg-gray-200 text-gray-800 text-sm font-semibold py-1.5 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isProcessing ? "Processing..." : "Not now"}
          </button>
        </div>
      )}
    </div>
  );
};

const NOTIFICATIONS_PER_PAGE = 20;

const NotificationsPage: React.FC = () => {
  usePageTracking("Notifications", "NotificationsPage");
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();
  const { notifications } = useActivityNotificationsData();
  const {
    markJoinNotificationsAsRead,
    markNotificationsAsReadByType,
    markAllNotificationsAsRead,
  } = useActivityActions();

  // Pagination state
  const [displayedNotifications, setDisplayedNotifications] = useState<
    Notification[]
  >([]);
  const [displayedActionNotifications, setDisplayedActionNotifications] =
    useState<Notification[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [showUnreadOnly, setShowUnreadOnly] = useState(true);
  const observerTarget = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      markJoinNotificationsAsRead();
      notifications.forEach((n) => {
        if (n.type === "FOLLOW_ACCEPT") {
          markNotificationsAsReadByType("FOLLOW_ACCEPT", n.fromUser.id);
        }
        if (n.type === "FOLLOW_DECLINE") {
          markNotificationsAsReadByType("FOLLOW_DECLINE", n.fromUser.id);
        }
      });
    }, 1000);
    return () => clearTimeout(timer);
  }, [
    markJoinNotificationsAsRead,
    notifications,
    markNotificationsAsReadByType,
  ]);

  if (!currentUser) return null;

  // Get filter type from URL params
  const urlParams = new URLSearchParams(location.search);
  const filterType = urlParams.get("type");

  // Separate notifications that require actions
  const actionNotifications = notifications
    .filter((n) => n.userId === currentUser.id)
    .filter(
      (n) =>
        (n.type === "FOLLOW_REQUEST" ||
          n.type === "REJOIN_REQUEST" ||
          n.type === "ACTIVITY_INVITE" ||
          n.type === "MATCH_SUGGESTION") &&
        !n.isRead,
    )
    .sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );

  const regularNotifications = notifications
    .filter((n) => n.userId === currentUser.id)
    .filter((n) => {
      // Exclude pending follow requests from regular notifications
      if (n.type === "FOLLOW_REQUEST" && !n.isRead) {
        return false;
      }

      // Exclude pending rejoin requests from regular notifications
      if (n.type === "REJOIN_REQUEST" && !n.isRead) {
        return false;
      }

      // Exclude pending activity invites from regular notifications
      if (n.type === "ACTIVITY_INVITE" && !n.isRead) {
        return false;
      }

      // Exclude pending match suggestions from regular notifications
      if (n.type === "MATCH_SUGGESTION" && !n.isRead) {
        return false;
      }

      if (n.type === "DIRECT_CHAT" || n.type === "MESSAGE") {
        return false; // CHG20251225: Do not show direct chat notifications in regular list
      }

      // Apply type filter if specified
      if (filterType === "follow_requests") {
        return n.type === "FOLLOW_REQUEST" && !n.isRead;
      } else if (filterType === "rejoin_requests") {
        return n.type === "REJOIN_REQUEST" && !n.isRead;
      } else if (filterType === "activities") {
        return [
          "JOIN",
          "ACTIVITY_UPDATE",
          "ACTIVITY_DELETE",
          "ACTIVITY_FULL",
          "ACTIVITY_KICK",
          "ACTIVITY_INVITE",
          "ACTIVITY_INVITE_ACCEPT",
          "ACTIVITY_INVITE_DECLINE",
          "ACTIVITY_INVITE_CANCEL",
          "MATCH_SUGGESTION",
        ].includes(n.type);
      } else if (filterType === "social") {
        return [
          "FOLLOW_REQUEST",
          "FOLLOW_ACCEPT",
          "FOLLOW_DECLINE",
          "MUTUAL_MATCH",
          "NEW_STORY",
          "FOLLOWER_REMOVED",
          "REJOIN_REQUEST",
          "REJOIN_APPROVED",
          "REJOIN_DECLINED",
        ].includes(n.type);
      }

      // Hide old handled follow requests (older than 7 days) for default view
      if (n.type === "FOLLOW_REQUEST" && n.isRead) {
        const notificationAge = Date.now() - new Date(n.createdAt).getTime();
        const sevenDaysInMs = 7 * 24 * 60 * 60 * 1000;
        return notificationAge < sevenDaysInMs;
      }

      // Hide old handled rejoin requests (older than 7 days) for default view
      if (n.type === "REJOIN_REQUEST" && n.isRead) {
        const notificationAge = Date.now() - new Date(n.createdAt).getTime();
        const sevenDaysInMs = 7 * 24 * 60 * 60 * 1000;
        return notificationAge < sevenDaysInMs;
      }

      // Hide old handled activity invites (older than 7 days) for default view
      if (n.type === "ACTIVITY_INVITE" && n.isRead) {
        const notificationAge = Date.now() - new Date(n.createdAt).getTime();
        const sevenDaysInMs = 7 * 24 * 60 * 60 * 1000;
        return notificationAge < sevenDaysInMs;
      }

      // Hide old handled match suggestions (older than 7 days) for default view
      if (n.type === "MATCH_SUGGESTION" && n.isRead) {
        const notificationAge = Date.now() - new Date(n.createdAt).getTime();
        const sevenDaysInMs = 7 * 24 * 60 * 60 * 1000;
        return notificationAge < sevenDaysInMs;
      }
      return true;
    })
    .sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );

  // Group activity-related notifications by activityId
  const activityTypes = [
    "JOIN",
    "ACTIVITY_UPDATE",
    "ACTIVITY_DELETE",
    "ACTIVITY_FULL",
    "ACTIVITY_KICK",
    "LEAVE",
  ];
  const groupedByActivity = new Map<string, Notification[]>();
  const nonActivityNotifications: Notification[] = [];

  regularNotifications.forEach((n) => {
    // Group only activity-related notifications that have an activityId
    if (activityTypes.includes(n.type) && n.activityId) {
      const existing = groupedByActivity.get(n.activityId) || [];
      existing.push(n);
      groupedByActivity.set(n.activityId, existing);
    } else {
      nonActivityNotifications.push(n);
    }
  });

  // Create grouped notification objects
  const groupedNotifications = Array.from(groupedByActivity.entries()).map(
    ([activityId, notifs]) => {
      // Sort by most recent first
      const sorted = notifs.sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      );
      const latest = sorted[0];

      return {
        ...latest,
        count: sorted.length,
        grouped: true,
        allNotifications: sorted,
        // Update link to go to activity (history) tab
        link: `/activity/${activityId}?section=activity`,
      };
    },
  );

  // Combine grouped and non-grouped notifications, sort by most recent
  const combinedNotifications = [
    ...groupedNotifications,
    ...nonActivityNotifications,
  ].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );

  // Apply unread filter if enabled
  // Filter out duplicate notifications by id
  const dedupedNotifications = Array.from(
    new Map(combinedNotifications.map((n) => [n.id, n])).values(),
  );
  const userNotifications = showUnreadOnly
    ? dedupedNotifications.filter((n) => {
        // For grouped notifications, show if any in the group is unread
        if ((n as any).grouped && (n as any).allNotifications) {
          return (n as any).allNotifications.some(
            (notif: Notification) => !notif.isRead,
          );
        }
        return !n.isRead;
      })
    : dedupedNotifications
        .flatMap((n) => {
          // When showing all notifications, expand grouped notifications to show each individual one
          if ((n as any).grouped && (n as any).allNotifications) {
            return (n as any).allNotifications.map((notif: Notification) => ({
              ...notif,
              // Update link to point to activity history tab
              link: notif.activityId
                ? `/activity/${notif.activityId}?section=activity`
                : notif.link,
            }));
          }
          // For non-grouped activity notifications, also ensure they point to activity section
          if (activityTypes.includes(n.type) && n.activityId) {
            return [
              {
                ...n,
                link: `/activity/${n.activityId}?section=activity`,
              },
            ];
          }
          return [n];
        })
        .sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        );

  const unreadCount = regularNotifications.filter((n) => !n.isRead).length;

  // Load more notifications callback
  const loadMoreNotifications = useCallback(() => {
    if (isLoadingMore || !hasMore) return;

    setIsLoadingMore(true);

    setTimeout(() => {
      const currentLength = displayedNotifications.length;
      const nextBatch = userNotifications.slice(
        currentLength,
        currentLength + NOTIFICATIONS_PER_PAGE,
      );

      if (nextBatch.length > 0) {
        setDisplayedNotifications((prev) => [...prev, ...nextBatch]);
        setHasMore(currentLength + nextBatch.length < userNotifications.length);
      } else {
        setHasMore(false);
      }

      setIsLoadingMore(false);
    }, 300);
  }, [
    isLoadingMore,
    hasMore,
    displayedNotifications.length,
    userNotifications,
  ]);

  const handleMarkAllAsRead = async () => {
    await markAllNotificationsAsRead();
  };

  // Initialize displayed notifications when userNotifications changes
  useEffect(() => {
    const initialBatch = userNotifications.slice(0, NOTIFICATIONS_PER_PAGE);
    setDisplayedNotifications(initialBatch);
    setHasMore(userNotifications.length > NOTIFICATIONS_PER_PAGE);

    // Initialize action notifications (show all)
    setDisplayedActionNotifications(actionNotifications);
  }, [notifications, filterType, showUnreadOnly]);

  // Intersection observer for infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoadingMore) {
          loadMoreNotifications();
        }
      },
      { threshold: 0.1 },
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [hasMore, isLoadingMore, loadMoreNotifications]);

  // Get title based on filter type
  const getTitle = () => {
    switch (filterType) {
      case "follow_requests":
        return "Follow Requests";
      case "rejoin_requests":
        return "Rejoin Requests";
      case "messages":
        return "Message Notifications";
      case "activities":
        return "Activity Notifications";
      case "social":
        return "Social Notifications";
      default:
        return "Notifications";
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-white">
      <header
        className="flex-shrink-0 bg-white px-3 pb-3 pt-3 flex items-center shadow-sm border-b border-gray-200"
        style={{ position: "sticky", top: "0", zIndex: 20 }}
      >
        <button
          onClick={() => navigate(-1)}
          className="text-gray-500 mr-4 p-2 rounded-full hover:bg-gray-100"
        >
          <ArrowLeftIcon className="w-6 h-6" />
        </button>
        <h1 className="text-xl font-bold text-gray-900">{getTitle()}</h1>
      </header>

      <main className="flex-1 overflow-y-auto hide-scrollbar page-content-top">
        <div className="max-w-3xl mx-auto p-4">
          {/* Filter buttons */}
          <div className="flex items-center gap-2 mb-4">
            <button
              onClick={() => setShowUnreadOnly(!showUnreadOnly)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                showUnreadOnly
                  ? "bg-primary text-white"
                  : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
              }`}
            >
              <EyeIcon className="w-4 h-4" />
              {showUnreadOnly ? "Show All" : `Unread (${unreadCount})`}
            </button>

            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllAsRead}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 transition-colors"
              >
                <CheckIcon className="w-4 h-4" />
                Mark All Read
              </button>
            )}
          </div>

          {/* Action Required Section */}
          {displayedActionNotifications.length > 0 && (
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3">
                <BellIcon className="w-5 h-5 text-yellow-500" />
                <h2 className="text-lg font-semibold text-gray-900">
                  Action Required
                </h2>
                <span className="bg-yellow-100 text-yellow-800 text-xs font-medium px-2 py-0.5 rounded-full">
                  {displayedActionNotifications.length}
                </span>
              </div>
              <div className="space-y-3">
                {displayedActionNotifications.map((n) => (
                  <NotificationItem key={n.id} notification={n} />
                ))}
              </div>
            </div>
          )}

          {/* Regular Notifications */}
          <div className="space-y-3">
            {displayedNotifications.length > 0 ? (
              <>
                {displayedNotifications.map((n) => (
                  <NotificationItem key={n.id} notification={n} />
                ))}

                {/* Intersection observer target */}
                {hasMore && (
                  <div
                    ref={observerTarget}
                    className="flex justify-center py-4"
                  >
                    {isLoadingMore && (
                      <div className="animate-pulse text-gray-400 text-sm">
                        Loading more...
                      </div>
                    )}
                  </div>
                )}

                {!hasMore &&
                  userNotifications.length > NOTIFICATIONS_PER_PAGE && (
                    <div className="text-center text-gray-400 text-sm py-4">
                      All notifications loaded
                    </div>
                  )}
              </>
            ) : (
              <div className="text-center text-gray-500 pt-20">
                <p>No notifications yet.</p>
                <p className="text-sm">
                  We'll let you know when something's up!
                </p>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default NotificationsPage;
